import { z } from 'zod';
export const SEARCH_DOCUMENT_KINDS = [
    'digest',
    'item',
    'content',
    'research',
    'note',
    'todo',
    'topic',
    'doc',
    'package',
];
export const SEARCH_CONTENT_TYPES = [
    'research',
    'note',
    'todo',
    'topic',
    'doc',
    'package',
    'digest',
    'item',
    'item_list',
    'markdown',
    'callout',
    'metric_grid',
    'table',
    'timeline',
    'link_list',
    'key_value',
    'quote',
    'bar_chart',
    'stacked_bar_chart',
    'diagram',
    'unsupported_visualization_request',
];
export const SEARCH_STATUSES = ['active', 'done', 'not_relevant', 'snoozed'];
export const SearchResultSchema = z.object({
    id: z.string(),
    kind: z.enum(SEARCH_DOCUMENT_KINDS),
    contentType: z.enum(SEARCH_CONTENT_TYPES),
    title: z.string(),
    excerpt: z.string().nullable(),
    date: z.string(),
    sectionId: z.string().nullable(),
    blockIndex: z.number().int().nullable(),
    itemId: z.string().nullable(),
    sourceType: z.string().nullable(),
    status: z.enum(SEARCH_STATUSES).nullable(),
    href: z.string(),
    anchor: z.string(),
    score: z.number(),
});
const CalendarDateSchema = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/u)
    .refine((value) => {
    const [year, month, day] = value.split('-').map(Number);
    const parsed = new Date(Date.UTC(year, month - 1, day));
    return (parsed.getUTCFullYear() === year &&
        parsed.getUTCMonth() === month - 1 &&
        parsed.getUTCDate() === day);
}, 'Invalid calendar date');
export const SearchQuerySchema = z.object({
    q: z.string().trim().min(1).max(300),
    kinds: z.array(z.enum(SEARCH_DOCUMENT_KINDS)).default([]),
    contentTypes: z.array(z.enum(SEARCH_CONTENT_TYPES)).default([]),
    sources: z.array(z.string().trim().min(1).max(80)).default([]),
    statuses: z.array(z.enum(SEARCH_STATUSES)).default([]),
    dateFrom: CalendarDateSchema.optional(),
    dateTo: CalendarDateSchema.optional(),
    limit: z.number().int().min(1).max(50).default(24),
});
export function searchDocumentAnchor(date, pointer) {
    const suffix = pointer.split('/').filter(Boolean).join('-');
    return suffix ? `search-${date}-${suffix}` : `search-${date}-digest`;
}
function compact(parts) {
    return parts
        .filter((part) => part !== undefined && part !== null && part !== '')
        .join(' ');
}
function simpleBlockText(block) {
    switch (block.type) {
        case 'markdown':
            return { title: 'Notes', body: block.body, metadata: '' };
        case 'item_list':
            return { title: block.title ?? 'Items', body: block.note ?? '', metadata: '' };
        case 'callout':
            return { title: block.title ?? 'Callout', body: block.body, metadata: block.variant };
        case 'metric_grid':
            return {
                title: block.title ?? 'Metrics',
                body: block.metrics
                    .map((metric) => compact([metric.label, metric.value, metric.hint]))
                    .join('\n'),
                metadata: block.metrics
                    .map((metric) => metric.state)
                    .filter(Boolean)
                    .join(' '),
            };
        case 'table':
            return {
                title: block.title ?? 'Table',
                body: [block.columns.join(' '), ...block.rows.map((row) => row.join(' '))].join('\n'),
                metadata: '',
            };
        case 'timeline':
            return {
                title: block.title ?? 'Timeline',
                body: block.entries
                    .map((entry) => compact([entry.time, entry.title, entry.detail, entry.tag]))
                    .join('\n'),
                metadata: block.entries
                    .map((entry) => entry.emphasis)
                    .filter(Boolean)
                    .join(' '),
            };
        case 'link_list':
            return {
                title: block.title ?? 'Links',
                body: block.links.map((link) => `${link.label} ${link.href}`).join('\n'),
                metadata: '',
            };
        case 'key_value':
            return {
                title: block.title ?? 'Details',
                body: block.pairs.map((pair) => `${pair.key} ${pair.value}`).join('\n'),
                metadata: '',
            };
        case 'quote':
            return { title: block.cite ?? 'Quote', body: block.body, metadata: block.cite ?? '' };
        case 'bar_chart':
            return {
                title: block.title ?? 'Chart',
                body: block.bars
                    .map((bar) => compact([bar.label, bar.value, bar.percent, bar.state]))
                    .join('\n'),
                metadata: '',
            };
        case 'stacked_bar_chart':
            return {
                title: block.title ?? 'Chart',
                body: block.bars
                    .map((bar) => compact([
                    bar.label,
                    bar.value,
                    ...bar.segments.flatMap((segment) => [segment.label, segment.value, segment.state]),
                ]))
                    .join('\n'),
                metadata: compact([block.unit, ...(block.legend ?? []).map((entry) => entry.label)]),
            };
    }
}
function blockText(block) {
    if (block.type === 'diagram') {
        const fallback = simpleBlockText(block.fallback_block);
        return {
            title: block.title ?? fallback.title,
            body: compact([block.code, fallback.body]),
            metadata: compact(['mermaid', fallback.metadata]),
        };
    }
    if (block.type === 'unsupported_visualization_request') {
        const fallback = simpleBlockText(block.fallback_block);
        return {
            title: fallback.title,
            body: compact([block.reason, fallback.body]),
            metadata: compact([block.requested_type, block.signature, fallback.metadata]),
        };
    }
    return simpleBlockText(block);
}
/** Extract stable, deep-linkable search units from the digest SDUI document. */
export function extractDigestSearchDocuments(report) {
    const documents = [
        {
            documentId: `${report.date}:digest`,
            kind: 'digest',
            contentType: 'digest',
            date: report.date,
            sectionId: null,
            blockIndex: null,
            itemId: null,
            pointer: '',
            title: report.title ?? `Daily digest ${report.date}`,
            body: Object.values(report.sections)
                .map((section) => section.title)
                .join('\n'),
            sourceType: null,
            sourceRef: null,
            metadataText: report.sources.join(' '),
        },
    ];
    for (const [sectionId, section] of Object.entries(report.sections)) {
        section.blocks.forEach((block, blockIndex) => {
            const pointer = `/sections/${sectionId}/blocks/${blockIndex}`;
            const text = blockText(block);
            documents.push({
                documentId: `${report.date}:${pointer}`,
                kind: 'content',
                contentType: block.type,
                date: report.date,
                sectionId,
                blockIndex,
                itemId: null,
                pointer,
                title: text.title,
                body: text.body,
                sourceType: null,
                sourceRef: null,
                metadataText: compact([section.title, text.metadata]),
            });
            if (block.type !== 'item_list')
                return;
            block.items.forEach((item, itemIndex) => {
                const itemPointer = `${pointer}/items/${itemIndex}`;
                documents.push({
                    documentId: `${report.date}:${itemPointer}`,
                    kind: 'item',
                    contentType: 'item',
                    date: report.date,
                    sectionId,
                    blockIndex,
                    itemId: item.item_id ?? item.mirrors_item_id ?? null,
                    pointer: itemPointer,
                    title: item.title,
                    body: compact([item.detail, item.meta]),
                    sourceType: item.source_type?.toLocaleLowerCase('en-US') ?? null,
                    sourceRef: item.source_ref ?? null,
                    metadataText: compact([
                        section.title,
                        block.title,
                        item.src,
                        item.link,
                        item.action_label,
                        item.action_link,
                        item.context_label,
                        item.context_link,
                        item.semantic_slot,
                    ]),
                });
            });
        });
    }
    return documents;
}
